import { useState, useEffect } from 'react';
import { authController } from '../../controllers/authController';
import { useGoogleLogin } from '@react-oauth/google';
import { getOrCreateDeviceId, getDeviceName, DEVICE_TYPE } from '../../utils/device';
import { getAddressIfPermitted } from '../../utils/location';
import { getFcmTokenIfAvailable } from '../../utils/fcm';
import OtpVerification from './Otpverification'; // 🆕 OTP / phone number step

const getDefaultCompanyId = () => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEFAULT_COMPANY_ID) {
      return import.meta.env.VITE_DEFAULT_COMPANY_ID;
    }
  } catch (e) { /* CRA mein import.meta nahi hota, ignore karo */ }

  if (typeof process !== 'undefined' && process.env?.REACT_APP_DEFAULT_COMPANY_ID) {
    return process.env.REACT_APP_DEFAULT_COMPANY_ID;
  }

  return null;
};

// 🆕 Phone ko 10-digit mein normalize karo (+91, space, hyphen hata do)
const normalizePhone = (v) => String(v || '').replace(/\D/g, '').slice(-10);
const isValidMobile  = (v) => /^[6-9]\d{9}$/.test(normalizePhone(v));

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const EMPTY_FORM = { firstName: '', lastName: '', email: '', password: '', confirmPassword: '', phone: '' };

const Register = ({ isOpen, onClose, onSwitchToLogin, onRegisterSuccess }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  // 🆕 OTP step: { ...backend response, source: 'register' | 'google' }
  const [challenge, setChallenge] = useState(null);

  const [refParams, setRefParams] = useState({ companyId: null, parentId: null, role: 'retailer' });
  const [linkInvalid, setLinkInvalid] = useState(false);

  // 🆕 Modal band ho to OTP step reset karo
  useEffect(() => {
    if (!isOpen) setChallenge(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(window.location.search);

    const companyId = params.get('companyId') || getDefaultCompanyId() || null;
    const parentId  = params.get('parentId') || null;
    const role      = params.get('role') || 'retailer';

    setRefParams({ companyId, parentId, role });
    setLinkInvalid(false); // ab kabhi form block nahi hota — backend fallback resolve karega
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // 🆕 deviceId, deviceName, address, fcmToken — sab automatically nikalne
  // wala common helper, register aur Google-signup dono use karte hain
  const collectDeviceAndAddressData = async () => {
    const deviceId   = getOrCreateDeviceId();
    const deviceName = getDeviceName();
    const deviceType = DEVICE_TYPE;

    // Ye dono parallel mein chalte hain — koi bhi fail ho to poora form block nahi hota
    const [address, fcmToken] = await Promise.all([
      getAddressIfPermitted(),      // permission na ho to null aayega, koi error nahi
      getFcmTokenIfAvailable(),     // Firebase set up na ho to abhi null hi aayega
    ]);

    return { deviceId, deviceName, deviceType, address: address || undefined, fcmToken: fcmToken || undefined };
  };

  // 🆕 Register / Google signup poora hua (seedha ya OTP verify ke baad)
  const finishSignup = (user) => {
    setFormData(EMPTY_FORM);
    setChallenge(null);
    if (onRegisterSuccess && user) onRegisterSuccess(user);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // 🆕 Phone optional hai, par diya ho to sahi hona chahiye (is par OTP jaayega)
    if (formData.phone && !isValidMobile(formData.phone)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError('');

    // 🆕 deviceId/deviceName/address/fcmToken automatically collect karo
    const extra = await collectDeviceAndAddressData();

    const result = await authController.register({
      firstName: formData.firstName.trim(),
      lastName:  formData.lastName.trim(),
      email:     formData.email,
      password:  formData.password,
      phone:     formData.phone ? normalizePhone(formData.phone) : undefined,
      role:      refParams.role,
      companyId: refParams.companyId,
      parentId:  refParams.parentId,
      ...extra, // deviceId, deviceName, deviceType, address, fcmToken
    });

    if (result.success && result.otpRequired) {
      // 🆕 Account ban gaya, number par OTP gaya — verify hone par hi login hoga
      setChallenge({ ...result.data, source: 'register' });
    } else if (result.success) {
      finishSignup(result.data.user);
    } else {
      setError(result.message || 'Registration failed. Please try again.');
    }

    setLoading(false);
  };

  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true); setError('');
      try {
        const profileRes = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        if (!profileRes.ok) throw new Error('Google profile fetch failed');
        const profile = await profileRes.json();

        // 🆕 yahan bhi deviceId/address/fcmToken bhej rahe hain
        const extra = await collectDeviceAndAddressData();

        // 🆕 Google se phone nahi aata; form mein number bhara ho to wahi bhejo, warna backend number maangega
        const googlePhone = isValidMobile(formData.phone) ? normalizePhone(formData.phone) : '';

        const result = await authController.loginWithGoogle({
          googleId:  profile.sub,
          email:     profile.email,
          phone:     googlePhone,
          firstName: profile.given_name || profile.name?.split(' ')[0] || 'User',
          lastName:  profile.family_name || '',
          avatar:    profile.picture,
          profile,
          companyId: refParams.companyId,
          parentId:  refParams.parentId,
          role:      refParams.role,
          ...extra,
        });

        if (result?.success && result.otpRequired) {
          // 🆕 OTP screen, ya (data.phoneRequired) number maangne wali screen
          setChallenge({ ...result.data, source: 'google' });
        } else if (result?.success) {
          finishSignup(result.data?.user);
        } else {
          setError(result?.message || 'Google sign-up failed. Please try again.');
        }
      } catch (err) {
        console.error('[Google Signup]', err);
        setError('Google sign-up failed. Please try again.');
      }
      setGoogleLoading(false);
    },
    onError: () => {
      setError('Google sign-up was cancelled or failed.');
      setGoogleLoading(false);
    },
  });

  const handleGoogleSignup = () => {
    setGoogleLoading(true);
    googleSignup();
  };

  if (!isOpen) return null;

  const anyLoading = loading || googleLoading;

  const styles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 2000,
    },
    modal: {
      backgroundColor: '#ffffff', borderRadius: '12px', padding: '40px',
      width: '90%', maxWidth: '400px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      position: 'relative', maxHeight: '90vh', overflowY: 'auto',
    },
    closeButton: {
      position: 'absolute', top: '15px', right: '15px',
      background: 'transparent', border: 'none',
      fontSize: '24px', cursor: 'pointer', color: '#6b7280',
    },
    title: {
      fontSize: '28px', fontWeight: 'bold', color: '#1f2937',
      marginBottom: '24px', textAlign: 'center',
    },
    error: {
      backgroundColor: '#fee2e2', color: '#dc2626', padding: '12px',
      borderRadius: '8px', marginBottom: '20px', fontSize: '14px', textAlign: 'center',
    },
    formContainer: { display: 'flex', flexDirection: 'column', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    nameRow: { display: 'flex', gap: '12px' },
    label: { fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: {
      padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
      fontSize: '16px', outline: 'none', transition: 'border-color 0.3s',
      width: '100%', boxSizing: 'border-box',
    },
    hint: { fontSize: '12px', color: '#9ca3af', marginTop: '-2px' },
    button: {
      backgroundColor: '#2563eb', color: '#ffffff', padding: '14px',
      borderRadius: '8px', border: 'none', fontWeight: '600', fontSize: '16px',
      cursor: 'pointer', transition: 'background-color 0.3s', marginTop: '10px',
    },
    buttonDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
    divider: { display: 'flex', alignItems: 'center', gap: '12px', color: '#9ca3af', fontSize: '13px', margin: '4px 0' },
    divLine: { flex: 1, height: '1px', backgroundColor: '#e5e7eb' },
    googleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
      padding: '13px', borderRadius: '8px', border: '1.5px solid #d1d5db',
      backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', color: '#374151',
      cursor: 'pointer', transition: 'background-color 0.2s, border-color 0.2s', width: '100%',
    },
    loginLink: { textAlign: 'center', marginTop: '20px', color: '#6b7280', fontSize: '14px' },
    loginButton: {
      background: 'transparent', border: 'none', color: '#2563eb',
      fontWeight: '600', cursor: 'pointer', textDecoration: 'underline',
    },
  };

  // 🆕 OTP screen se wapas: register ke baad account ban chuka hai, isliye "Login" par bhejte hain
  // (login par phir OTP aayega aur number verify ho jaayega). Google flow mein sirf form par wapas.
  const handleOtpBack = () => {
    const wasRegister = challenge?.source === 'register';
    setChallenge(null);
    setError('');
    if (wasRegister && onSwitchToLogin) onSwitchToLogin();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeButton} onClick={onClose}>×</button>

        {challenge ? (
          <OtpVerification
            challenge={challenge}
            backLabel={challenge.source === 'register' ? 'Verify later — go to Login' : '← Back'}
            onVerified={(result) => finishSignup(result.data?.user)}
            onExpired={() => {
              const wasRegister = challenge?.source === 'register';
              setChallenge(null);
              setError(wasRegister
                ? 'OTP session expired. Your account is created — please login to verify your number.'
                : 'OTP session expired. Please try again.');
            }}
            onBack={handleOtpBack}
          />
        ) : (
          <>
            <h2 style={styles.title}>Register</h2>

            {error && <div style={styles.error}>{error}</div>}

            <form style={styles.formContainer} onSubmit={handleSubmit}>
              <div style={styles.nameRow}>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text" name="firstName" style={styles.input}
                    value={formData.firstName} onChange={handleChange}
                    placeholder="First name" disabled={anyLoading}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text" name="lastName" style={styles.input}
                    value={formData.lastName} onChange={handleChange}
                    placeholder="Last name" disabled={anyLoading}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email" name="email" style={styles.input}
                  value={formData.email} onChange={handleChange}
                  placeholder="Enter your email" disabled={anyLoading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone (optional)</label>
                <input
                  type="tel" name="phone" style={styles.input}
                  value={formData.phone} onChange={handleChange}
                  placeholder="10-digit phone number" disabled={anyLoading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <span style={styles.hint}>We'll send an OTP to this number. If you leave it empty, you'll be asked for it in the next step.</span>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password" name="password" style={styles.input}
                  value={formData.password} onChange={handleChange}
                  placeholder="At least 8 characters" disabled={anyLoading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm Password</label>
                <input
                  type="password" name="confirmPassword" style={styles.input}
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="Confirm your password" disabled={anyLoading}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <button
                type="submit" disabled={anyLoading}
                style={{ ...styles.button, ...(anyLoading ? styles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !anyLoading && (e.target.style.backgroundColor = '#1d4ed8')}
                onMouseLeave={(e) => !anyLoading && (e.target.style.backgroundColor = '#2563eb')}
              >
                {loading ? 'Registering...' : 'Register'}
              </button>
            </form>

            <div style={styles.divider}>
              <div style={styles.divLine} />
              or continue with
              <div style={styles.divLine} />
            </div>

            <button
              style={{ ...styles.googleBtn, ...(anyLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
              onClick={handleGoogleSignup}
              disabled={anyLoading}
              onMouseEnter={(e) => { if (!anyLoading) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.borderColor = '#9ca3af'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#d1d5db'; }}
            >
              <GoogleIcon />
              {googleLoading ? 'Connecting to Google...' : 'Sign up with Google'}
            </button>

            <div style={styles.loginLink}>
              Already have an account?{' '}
              <button style={styles.loginButton} onClick={onSwitchToLogin} disabled={anyLoading}>
                Login here
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;